/* ==========================================================================
   cn-atc-feedback.js — confirm an Add to Cart that the visitor cannot see.

   WHY THIS EXISTS (measured, not assumed — GA4, 30 days to 2 Sep 2026):

       view_item        247 events / 89 sessions
       add_to_cart       37 events /  5 sessions   <- 7.4 clicks per person
       view_cart          2 events /  1 session
       purchase           0

   Five people clicked Add to Cart 37 times between them. Reproduced on the
   live storefront: the add SUCCEEDS (/cart.js item_count 0 -> 1) but nothing
   on screen changes. No drawer, no toast, no navigation. The only feedback is
   the header cart badge, and at the moment of clicking it measures
   top: -407px — 407 pixels ABOVE the viewport, because the visitor is scrolled
   down at the product form. They see nothing, so they click again.

   WHAT THIS DOES: inserts a confirmation directly beneath the button row,
   where the eye already is, and offers the next step (View cart).

   WHY IT HOOKS fetch RATHER THAN THE BUTTON: this theme has TWO add-to-cart
   handlers — one in assets/theme.js and one inline in sections/main_product
   .liquid — and both build the payload by hand. Wrapping fetch catches a
   success from either without touching that 60 KB section file. A click
   fallback covers any path that does not use fetch.

   SAFETY: the fetch wrapper returns the ORIGINAL promise untouched and never
   reads the response body (that would lock the stream for the real handler).
   Every branch is wrapped in try/catch and fails silent — a broken
   confirmation must never break a working Add to Cart.

   NOTE: no regular expressions and no backslashes anywhere in this file, per
   the convention in assets/cn-configurator.js.
   ========================================================================== */

(function () {
  'use strict';

  var cfg = document.getElementById('CNAtcFeedbackConfig');
  if (!cfg) { return; }

  var cartUrl = cfg.getAttribute('data-cart-url') || '/cart';
  var txtAdded = cfg.getAttribute('data-label-added') || 'Added to your cart';
  var txtView = cfg.getAttribute('data-label-view') || 'View cart';
  var txtMore = cfg.getAttribute('data-label-more') || 'or keep browsing';

  var panel = null;
  var titleEl = null;
  var metaEl = null;
  var lastCount = null;

  /* ---------- build the panel once, beside the button ---------- */

  function anchor() {
    var row = document.querySelector('.cn-atc-row');
    if (row) { return row; }
    var btn = document.getElementById('CNAtcBtn');
    if (btn && btn.parentNode) { return btn; }
    return null;
  }

  function mount() {
    if (panel) { return panel; }
    var host = anchor();
    if (!host || !host.parentNode) { return null; }

    panel = document.createElement('div');
    panel.className = 'cn-atc-feedback';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.hidden = true;

    var mark = document.createElement('span');
    mark.className = 'cn-atc-feedback__mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '✓';

    var body = document.createElement('div');
    body.className = 'cn-atc-feedback__body';

    titleEl = document.createElement('p');
    titleEl.className = 'cn-atc-feedback__title';
    titleEl.textContent = txtAdded;

    metaEl = document.createElement('p');
    metaEl.className = 'cn-atc-feedback__meta';

    var actions = document.createElement('div');
    actions.className = 'cn-atc-feedback__actions';

    var link = document.createElement('a');
    link.className = 'cn-atc-feedback__cta';
    link.href = cartUrl;
    link.textContent = txtView;

    var hint = document.createElement('span');
    hint.className = 'cn-atc-feedback__hint';
    hint.textContent = txtMore;

    actions.appendChild(link);
    actions.appendChild(hint);
    body.appendChild(titleEl);
    body.appendChild(metaEl);
    body.appendChild(actions);
    panel.appendChild(mark);
    panel.appendChild(body);

    host.parentNode.insertBefore(panel, host.nextSibling);
    return panel;
  }

  /* ---------- reveal ---------- */

  function reveal(name, count) {
    var el = mount();
    if (!el) { return; }

    if (metaEl) {
      var line = '';
      if (name) { line = name; }
      if (count && count > 0) {
        line = line ? line + '  ·  ' + count + ' in cart' : count + ' in cart';
      }
      metaEl.textContent = line;
      metaEl.hidden = !line;
    }

    el.hidden = false;
    el.classList.add('is-visible');

    try {
      var box = el.getBoundingClientRect();
      if (box.bottom > window.innerHeight || box.top < 0) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    } catch (e) { /* positioning is a nicety, never a failure */ }
  }

  /* ---------- keep the header badge honest too ---------- */

  function paintCount(count) {
    if (typeof count !== 'number') { return; }
    var nodes = document.querySelectorAll(
      '[data-cart-count], .cart-count, .cart__count, [class*="cart-count"]'
    );
    var i;
    for (i = 0; i < nodes.length; i++) {
      try { nodes[i].textContent = String(count); } catch (e) { /* ignore */ }
    }
  }

  /* ---------- read the cart, then confirm ---------- */

  function confirmFromCart() {
    if (typeof window.fetch !== 'function') { return; }
    window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) { return; }
        var count = typeof cart.item_count === 'number' ? cart.item_count : null;
        /* Shopify returns cart.items NEWEST FIRST — verified on the v14
           preview: after adding the Mamluk to a cart already holding the
           Prussian Blue, items[0] was the Mamluk. Reading items[length - 1]
           named the wrong product, which is worse than naming none. */
        var name = '';
        if (cart.items && cart.items.length) {
          var newest = cart.items[0];
          if (newest) { name = newest.product_title || newest.title || ''; }
        }
        if (count !== null) {
          paintCount(count);
          lastCount = count;
        }
        reveal(name, count);
      })
      .catch(function () { /* fail silent */ });
  }

  /* ---------- 1. wrap fetch: catches both add-to-cart handlers ---------- */

  if (typeof window.fetch === 'function') {
    var original = window.fetch;
    window.fetch = function (input, init) {
      var promise = original.apply(this, arguments);
      try {
        var url = '';
        if (typeof input === 'string') {
          url = input;
        } else if (input && typeof input.url === 'string') {
          url = input.url;
        }
        if (url && url.indexOf('/cart/add') !== -1) {
          promise.then(
            function (res) {
              /* never read res here — the real handler still needs the body */
              try { if (res && res.ok) { confirmFromCart(); } } catch (e) { /* ignore */ }
              return res;
            },
            function (err) { return err; }
          );
        }
      } catch (e) { /* never interfere with the caller */ }
      return promise;
    };
  }

  /* ---------- 2. click fallback: covers any non-fetch path ---------- */

  function snapshotThenWatch() {
    if (typeof window.fetch !== 'function') { return; }
    window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (cart) {
        var before = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
        var tries = 0;
        var timer = window.setInterval(function () {
          tries = tries + 1;
          if (tries > 8) { window.clearInterval(timer); return; }
          window.fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r && r.ok ? r.json() : null; })
            .then(function (now) {
              if (!now || typeof now.item_count !== 'number') { return; }
              if (before === null || now.item_count > before) {
                window.clearInterval(timer);
                confirmFromCart();
              }
            })
            .catch(function () { /* ignore */ });
        }, 350);
      })
      .catch(function () { /* ignore */ });
  }

  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) { return; }
    var btn = t.closest('#CNAtcBtn, button[name="add"], .cn-atc-row button[type="submit"]');
    if (!btn) { return; }
    snapshotThenWatch();
  }, true);

}());
